import { TimeFrameKey, timeFrames } from 'data/time-frames';
import api from 'services/api';
import {
  ActiveProductDto,
  AssetAggregateDto,
  ProductGraphDto,
  ProductMetricsDto,
  ProductsApi,
  ProductsGraphsDto,
  ReferenceApi,
} from 'services/generated';
import { handleErrors } from 'services/helpers';
import { rootApi } from 'store/root-api';

const productsClient = new ProductsApi(undefined, undefined, api);
const referenceClient = new ReferenceApi(undefined, undefined, api);

export const productsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getAvailableProducts: builder.query<ActiveProductDto[], void>({
      queryFn: () => handleErrors(() => productsClient.getAvailableProducts()),
    }),
    getAvailableProductsGraphs: builder.query<
      ProductsGraphsDto,
      { timeFrame: TimeFrameKey; displayCurrency?: string; decimateBy?: number }
    >({
      queryFn: ({ timeFrame, displayCurrency, decimateBy }) =>
        handleErrors(() =>
          productsClient.getAvailableProductsGraphs(
            timeFrames[timeFrame].valueDays || undefined,
            displayCurrency,
            decimateBy,
          ),
        ),
    }),
    getProductGraph: builder.query<
      ProductGraphDto,
      {
        productCode: string;
        timeFrame: TimeFrameKey;
        displayCurrency?: string;
        decimateBy?: number;
      }
    >({
      queryFn: ({ productCode, timeFrame, displayCurrency, decimateBy }) =>
        handleErrors(() =>
          productsClient.getProductGraph(
            productCode,
            timeFrames[timeFrame].valueDays || undefined,
            displayCurrency,
            decimateBy,
          ),
        ),
    }),
    getAvailableProductsMetrics: builder.query<
      ProductMetricsDto[],
      { displayCurrency?: string }
    >({
      queryFn: ({ displayCurrency }) =>
        handleErrors(() =>
          productsClient.getAvailableProductsMetrics(displayCurrency),
        ),
    }),
    getProductDetails: builder.query<AssetAggregateDto, string>({
      queryFn: productCode =>
        handleErrors(() => referenceClient.getAsset(productCode)),
    }),
  }),
});

// We might want to create separate queries for this in the future
export function useGetProductMetricsQuery(
  productCode: string,
  displayCurrency?: string,
) {
  return useGetAvailableProductsMetricsQuery(
    { displayCurrency },
    {
      selectFromResult: ({ data, isLoading }) => ({
        data: data?.find(metric => metric.productCode === productCode),
        isLoading,
      }),
      skip: !productCode,
    },
  );
}

export const {
  useGetAvailableProductsQuery,
  useGetAvailableProductsGraphsQuery,
  useGetAvailableProductsMetricsQuery,
  useGetProductDetailsQuery,
  useGetProductGraphQuery,
} = productsApi;
